import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import AgGridReact from '../../../common/AgGrid/AgGridReact.jsx';
import { gridTheme } from '../../../common/AgGrid/gridTheme.js';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import { buildColumnDefs } from '../../../datagrid/hooks/useDynamicCols.jsx';
import { applyContentBlockData } from '../../redux/dashboardSlice.js';
import { selectWidgetEffectiveCols, setWidgetEffectiveCols } from '../../redux/datasourceSlice.js';
import { queryWidget } from '../../datasource_v2/api.js';
import { selectCrossFilter } from '../../datasource_v2/useDatasourceQuery.js';
import useDatasourceV2Channel from '../../datasource_v2/useDatasourceV2Channel.js';
import styles from './GridWidget.module.scss';

const PAGE_SIZE = 100;
const EMPTY_COLUMNS = [];

/**
 * Grid widget on the v2 datasource engine: the widget's saved query result, loaded page by page
 * (AG Grid server-side row model) with server side sorting and cross filters.
 */
function GridWidgetV2({ widgetId }) {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const dashboardId = useSelector((state) => state.dashboard.dashboardId);
  const datasourceId = useSelector((state) => state.dashboard.widgets[widgetId]?.datasource_v2_id);
  const savedQuery = useSelector((state) => state.dashboard.widgets[widgetId]?.datasource_query);
  const settings = useSelector((state) => state.dashboard.widgets[widgetId].settings);
  const userDefinedColumns = settings.columns || EMPTY_COLUMNS;
  const { useContextMenu } = settings;
  const targetWidgetId = settings.targetWidget?.enabled ? settings.targetWidget?.widgetId : null;
  const outputCols = useSelector(selectWidgetEffectiveCols(widgetId));
  const crossFilter = useSelector(selectCrossFilter(datasourceId, widgetId), isEqual);
  // Read in the row loader without re-creating it (it updates the output columns itself)
  const outputColsRef = useRef(outputCols);
  outputColsRef.current = outputCols;

  // Columns picked in the grid settings; all output columns until some are picked
  const columnDefs = useMemo(() => {
    const datasourceCols = buildColumnDefs(outputCols, { editMode: false });
    const picked = userDefinedColumns.length ? userDefinedColumns : outputCols.map((c) => ({ accessor: c.accessor, field: c.accessor }));
    return picked.map((c) => ({
      ...(datasourceCols.find((dc) => (dc.accessor || dc.field) === (c.accessor || c.field)) || {}),
      ...c,
      suppressHeaderContextMenu: !useContextMenu,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
    }));
  }, [userDefinedColumns, outputCols, useContextMenu]);

  const serverSideDatasource = useMemo(() => ({
    getRows(params) {
      const { startRow, endRow, sortModel } = params.request;
      const runtime = {
        filter: crossFilter,
        page: { offset: startRow, limit: endRow - startRow },
        sort: sortModel.map((s) => ({ column: s.colId, direction: s.sort })),
      };
      queryWidget(dashboardId, widgetId, { runtime })
        .then((res) => {
          if (!res.rows) {
            params.success({ rowData: [], rowCount: 0 });
            return;
          }
          if (res.cols && !isEqual(res.cols, outputColsRef.current)) dispatch(setWidgetEffectiveCols({ widgetId, cols: res.cols }));
          // Paged pivots have no total; the last page has fewer rows than requested
          const rowCount = res.rowCount ?? (res.rows.length < endRow - startRow ? startRow + res.rows.length : undefined);
          params.success({ rowData: res.rows, ...(rowCount != null && { rowCount }) });
        })
        .catch(() => params.fail());
    },
  // savedQuery: refetch when the data settings change
  }), [dashboardId, widgetId, crossFilter, savedQuery, dispatch]);

  useDatasourceV2Channel(datasourceId, () => gridRef.current?.api?.refreshServerSide({ purge: true }));

  // Grid rows can feed a content block widget, as in the legacy grid
  const onRowClicked = useCallback((params) => {
    if (!targetWidgetId) return;
    const data = Object.fromEntries(userDefinedColumns.filter((c) => c.contentToReplace).map((c) => [
      c.contentToReplace,
      params.api.getCellValue({ rowNode: params.node, colKey: c.accessor, useFormatter: true }) ?? '',
    ]));
    dispatch(applyContentBlockData({ targetWidgetId, data }));
  }, [targetWidgetId, userDefinedColumns, dispatch]);

  return (
    <div className={`${styles['grid-widget-container']} ${styles['grid-widget-v2']}`}>
      <div className='grid-theme mb-1 datasource-table flex-1'>
        <AgGridReact
          ref={gridRef}
          theme={gridTheme}
          columnDefs={columnDefs}
          rowModelType='serverSide'
          serverSideDatasource={serverSideDatasource}
          cacheBlockSize={PAGE_SIZE}
          maxConcurrentDatasourceRequests={2}
          loadingOverlayComponent={LoadingIndicator}
          onGridReady={(params) => params.api.sizeColumnsToFit()}
          onGridSizeChanged={(params) => params.api.sizeColumnsToFit()}
          onRowClicked={onRowClicked}
          {...(!useContextMenu ? { getContextMenuItems: () => [] } : {})}
        />
      </div>
    </div>
  );
}

export default GridWidgetV2;
